/**
 * @file Bloc d'acceptation des conditions générales dans le widget de réservation.
 * @module packages.common.components.table-reservation.components.LegalAcceptanceBox
 *
 * Affiche une checkbox bloquante pour les CG et un lien passif vers la politique
 * de confidentialité. Les deux liens ouvrent les pages Brydge dans un nouvel onglet
 * (target=_blank) afin de fonctionner correctement dans le contexte iframe.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  FormHelperText,
  Typography,
  Link,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const FALLBACK_PUBLIC_URL = 'https://go.brydge.app';

// ----------------------------------------------------------------------

/**
 * Bloc d'acceptation légale pour le widget de réservation.
 *
 * @param {Object} props
 * @param {boolean} props.checked - État de la checkbox CG
 * @param {Function} props.onChange - Callback appelé avec la nouvelle valeur booléenne
 * @param {string} [props.error] - Message d'erreur de validation
 * @param {Function} [props.t] - Fonction de traduction i18next
 * @param {string} [props.slug] - Slug de l'établissement pour construire les liens légaux
 * @return {JSX.Element} Bloc checkbox CG + lien politique de confidentialité
 */
export function LegalAcceptanceBox({checked, onChange, error, t, slug}) {
  const baseUrl = process.env.REACT_APP_PUBLIC_URL || FALLBACK_PUBLIC_URL;
  const termsUrl = slug ? `${baseUrl}/${slug}/reservation/terms` : null;
  const privacyUrl = slug ? `${baseUrl}/${slug}/privacy-policy` : null;

  const termsCheckboxPrefix = t ? t('widgets.reservation.legal.termsCheckboxPrefix') : 'J\'accepte les';
  const termsLinkText = t ? t('widgets.reservation.legal.termsLinkText') : 'conditions générales';
  const privacyLinkText = t ? t('widgets.reservation.legal.privacyLinkText') : 'Politique de confidentialité';

  return (
    <Box sx={{mt: 1}}>
      <Box sx={{display: 'flex', alignItems: 'flex-start', gap: '4px'}}>
        <Checkbox
          id="terms-checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          size="small"
          color={error ? 'error' : 'primary'}
          sx={{p: '2px', mt: '1px', flexShrink: 0}}
        />

        <Box>
          <Box
            component="label"
            htmlFor="terms-checkbox"
            sx={{cursor: 'pointer', display: 'block'}}
          >
            <Typography variant="body2" sx={{lineHeight: 1.5}}>
              {termsCheckboxPrefix}{' '}
              {termsUrl ? (
                <Link
                  href={termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    'color': 'text.primary',
                    'fontWeight': 500,
                    'textDecoration': 'underline',
                    'textDecorationColor': 'text.disabled',
                    '&:hover': {textDecorationColor: 'text.primary'},
                    'display': 'inline-flex',
                    'alignItems': 'center',
                    'gap': '2px',
                  }}
                >
                  {termsLinkText}
                  <OpenInNewIcon sx={{fontSize: '0.75rem', opacity: 0.6}} />
                </Link>
              ) : termsLinkText}
            </Typography>
          </Box>

          <Typography variant="caption" sx={{display: 'block', mt: 0.5}}>
            {privacyUrl ? (
              <Link
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  'color': 'text.disabled',
                  'textDecoration': 'none',
                  'display': 'inline-flex',
                  'alignItems': 'center',
                  'gap': '2px',
                  '&:hover': {'color': 'text.secondary', 'textDecoration': 'underline'},
                }}
              >
                {privacyLinkText}
                <OpenInNewIcon sx={{fontSize: '0.65rem'}} />
              </Link>
            ) : privacyLinkText}
          </Typography>
        </Box>
      </Box>

      {error && (
        <FormHelperText error sx={{ml: '32px', mt: 0}}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
}

LegalAcceptanceBox.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  t: PropTypes.func,
  slug: PropTypes.string,
};
